import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import AppLayout from "../components/layout/AppLayout";
import AuthLayout from "../components/layout/AuthLayout";
import Login from "../pages/Login";
import TransactionSchedulePage from "../pages/TransactionSchedulePage";
import TransactionScheduleDashboardPage from "../pages/TransactionScheduleDashboardPage";
import UploadExcelAcc from "../pages/UploadExcel";
import UsersPage from "../pages/UsersPage";
import InstitutionAccountsPage from "../pages/institution/accounts/InstitutionAccountsPage";
import HeadInstitutionAccountsPage from "../pages/institution/accounts/HeadInstitutionAccountsPage";
import TransactionDataTablePage from "../pages/TransactionDataTablePage";
import InstitutionWalletsPage from "../pages/institution/wallets/InstitutionWalletsPage";
import AccountPage from "../pages/account/AccountPage";
import HeadInstitutionControllPage from "../pages/HeadInstitutionControllPage";
import SearchStatementsPage from "../pages/SearchStatementsPage";
import InstitutionManageAccountsPage from "../pages/admin/institutions/InstitutionManageAccountsPage";
import InstitutionsPage from "../pages/admin/institutions/InstitutionsPage";
import InstitutionAnalyticsPage from "../pages/institution/analytics/InstitutionAnalyticsPage";
import TransactionPlanListPage from "../pages/transactionPlan/TransactionPlanListPage";
import { getTransactionPlanAccess } from "../components/transactionPlan/transactionPlanPermissions";

import ROUTE_PATHS from "./routePaths";
import { getUserRoles } from "../api/auth";
import {
  DISABLED_FALLBACK_ROUTE,
  isRouteDisabled,
} from "../config/disabledModules";

function AdminOnlyRoute({ children }) {
  const token = localStorage.getItem("access_token");
  const roles = getUserRoles(token).map((r) => String(r).replace("ROLE_", ""));
  const isAdmin = roles.includes("ADMIN");

  return isAdmin
    ? children
    : <Navigate to={DISABLED_FALLBACK_ROUTE} replace />;
}

/**
 * Closes a route that belongs to a temporarily disabled module. The page component is still
 * imported and still declared below, so re-enabling only means dropping the module from
 * config/disabledModules.js - no route or import has to be rewritten.
 */
function ModuleRoute({ path, children }) {
  return isRouteDisabled(path)
    ? <Navigate to={DISABLED_FALLBACK_ROUTE} replace />
    : children;
}

function TransactionPlanViewRoute({ children }) {
  return getTransactionPlanAccess().canView
    ? children
    : <Navigate to={ROUTE_PATHS.LOGIN} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route path={ROUTE_PATHS.LOGIN} element={<Login />} />
      </Route>

      <Route element={<AppLayout />}>
        <Route
          path={ROUTE_PATHS.TRANSACTION_SCHEDULE_DASHBOARD}
          element={
            <ModuleRoute path={ROUTE_PATHS.TRANSACTION_SCHEDULE_DASHBOARD}>
              <TransactionScheduleDashboardPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.TRANSACTION_SCHEDULE}
          element={
            <ModuleRoute path={ROUTE_PATHS.TRANSACTION_SCHEDULE}>
              <TransactionSchedulePage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.TRANSACTION_PLAN}
          element={
            <TransactionPlanViewRoute>
              <TransactionPlanListPage />
            </TransactionPlanViewRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.UPLOAD_EXCEL_ACC}
          element={
            <ModuleRoute path={ROUTE_PATHS.UPLOAD_EXCEL_ACC}>
              <UploadExcelAcc />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.TRANSACTION_DATA_TABLE}
          element={
            <ModuleRoute path={ROUTE_PATHS.TRANSACTION_DATA_TABLE}>
              <TransactionDataTablePage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.INSTITUTION_ACCOUNTS}
          element={
            <ModuleRoute path={ROUTE_PATHS.INSTITUTION_ACCOUNTS}>
              <InstitutionAccountsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.INSTITUTION_HEAD_ACCOUNTS}
          element={
            <ModuleRoute path={ROUTE_PATHS.INSTITUTION_HEAD_ACCOUNTS}>
              <HeadInstitutionAccountsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.INSTITUTION_WALLETS}
          element={
            <ModuleRoute path={ROUTE_PATHS.INSTITUTION_WALLETS}>
              <InstitutionWalletsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.INSTITUTION_ANALYTICS}
          element={
            <ModuleRoute path={ROUTE_PATHS.INSTITUTION_ANALYTICS}>
              <InstitutionAnalyticsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.MANAGE_INSTITUTIONS}
          element={
            <ModuleRoute path={ROUTE_PATHS.MANAGE_INSTITUTIONS}>
              <HeadInstitutionControllPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.ACCOUNT_DETAIL}
          element={
            <ModuleRoute path={ROUTE_PATHS.ACCOUNT_DETAIL}>
              <AccountPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.SEARCH_STATEMENTS}
          element={
            <ModuleRoute path={ROUTE_PATHS.SEARCH_STATEMENTS}>
              <SearchStatementsPage />
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.USERS}
          element={
            <AdminOnlyRoute>
              <UsersPage />
            </AdminOnlyRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.ADMIN_ACCOUNTS}
          element={
            <ModuleRoute path={ROUTE_PATHS.ADMIN_ACCOUNTS}>
              <AdminOnlyRoute>
                <InstitutionManageAccountsPage />
              </AdminOnlyRoute>
            </ModuleRoute>
          }
        />
        <Route
          path={ROUTE_PATHS.ADMIN_INSTITUTIONS}
          element={
            <ModuleRoute path={ROUTE_PATHS.ADMIN_INSTITUTIONS}>
              <AdminOnlyRoute>
                <InstitutionsPage />
              </AdminOnlyRoute>
            </ModuleRoute>
          }
        />
      </Route>

      <Route
        path="*"
        element={<Navigate to={DISABLED_FALLBACK_ROUTE} replace />}
      />
    </Routes>
  );
}

export default AppRouter;